/**
 * SOLID Review
 * S — Good. Two views of the top-level shell: `AuthRouter` (a lookup table from the screen to a lazy-loaded
 *     screen component, over its view model — the one-time `?join=`/`?payment=` start is the model's since
 *     frontend-framework-oo P1) and `App` (ErrorBoundary, MaintenanceBanner, the two providers,
 *     CookieConsentProvider and Suspense around it).
 * O — Good. A new non-GAME screen is a new `SCREEN_RENDERERS` table entry, not an edited
 *     branch — `AuthRouter`'s only remaining `if` is the GAME screen's genuinely different
 *     wrapping (`ComponentFrameworkProvider` instead of `Suspense`+`LoadingBoundary`), which
 *     a table entry can't express uniformly anyway.
 * L — N/A — no inheritance hierarchy.
 * I — Good. `App` takes no props; `AuthRouter` reads the screen from its view model (`AuthRouterViewModel`,
 *     which starts the session once per page — strict-ports FE2 — over the client view and one use case).
 *     Narrow, cohesive.
 * D — Depends on the boundary components, `MaintenanceBanner`, `CookieConsentProvider`
 *     and the client view, plus lazy imports of 6 screen components — all same-layer
 *     framework/application concerns, no direct infrastructure imports. Good. Now also
 *     wraps every screen in `ComponentFrameworkProvider`, handing it the composition root's
 *     ASSEMBLED Framework component and its host resources (strict-ports FE6a: until then
 *     the provider connected a fresh component from an adapter registry of Apollo, REST,
 *     LiveKit and health adapters, all moved behind the Application's use cases in FE1–FE5). It
 *     is NOT a composition root since strict-ports FE7: it wired the Apollo slow-request banner to
 *     `useUIStore` until then, which named an Infrastructure module; `frontendRoot` does that now. (It
 *     also wired the auth store's post-logout reset until FE3, when signing out became a decision whose
 *     changes reset the table.) Since frontend-framework-oo P1 it hands the tree the root's view-model
 *     factory too (`ViewModelProvider`).
 */
import React, { Suspense } from 'react';
import { ErrorBoundary } from './boundaries/ErrorBoundary';
import { LoadingBoundary } from './boundaries/LoadingBoundary';
import { MaintenanceBanner } from './components/layout/MaintenanceBanner';
import { CookieConsentProvider } from './consent/CookieConsentProvider';
import type { IEntitySessionView } from '../application/ports/ClientStateView';
import { ComponentFrameworkProvider } from './uml-components/ComponentFramework';
import { ViewModelProvider } from './viewmodels/ViewModelProvider';
import { useViewModel } from './viewmodels/useViewModel';
import { frontendGraph, frameworkHost, frameworkViews, checkServerHealth } from './composition/frontendRoot';

// Eager: Apollo is already loaded by AppRoot.tsx itself (see the lazy-import comment
// below), so routing its operations through a ComponentFramework adapter carries no
// extra bundle-size cost — unlike LiveKit's real-time client factory (liveKitClientFactory,
// below), this can be registered up front.

// The slow-request banner's wiring (apolloClient → the UI store) is the composition root's since strict-ports
// FE7: it names a concrete Infrastructure module, which only `framework/composition/` may
// (`request-adapters-no-infrastructure`). It runs in `frontendRoot`, at the same module load.

// The cookie-consent CMP is no longer wired here (strict-ports FE4b): `configureConsent` handed the
// provider a gateway and an audit service built at module load. The gateway is a host resource of
// Infrastructure now (`BrowserPlatform`), the audit record is `ClientLegalServer`'s, and the provider
// talks to the Application's use cases like every other component.

// Lazy: AppRoot statically imports Apollo (~179 kB) which is only needed in the GAME screen.
// Deferring it prevents that chunk from being parsed on the WelcomeScreen / auth screens.
const AppRoot = React.lazy(() =>
  import('./components/AppRoot').then((m) => ({ default: m.AppRoot }))
);

const WelcomeScreen = React.lazy(() => import('./components/auth/WelcomeScreen'));
const EnterCodeScreen = React.lazy(() => import('./components/auth/EnterCodeScreen'));
const GuestChoiceScreen = React.lazy(() => import('./components/auth/GuestChoiceScreen'));
const VerifyCodeScreen = React.lazy(() => import('./components/auth/VerifyCodeScreen'));
const LoginScreen = React.lazy(() => import('./components/auth/LoginScreen'));
const PaymentSuccessScreen = React.lazy(() => import('./components/payment/PaymentSuccessScreen'));

// Every screen except GAME (handled separately below — it needs ComponentFrameworkProvider,
// not Suspense+LoadingBoundary) and WELCOME (the fallback default) renders via this table.
// Adding a new screen is a new entry here, not a new branch in AuthRouter.
const SCREEN_RENDERERS: Partial<Record<IEntitySessionView['screen'], () => React.ReactElement>> = {
  PAYMENT_SUCCESS: () => <PaymentSuccessScreen />,
  ENTER_CODE: () => <EnterCodeScreen />,
  GUEST_CHOICE: () => <GuestChoiceScreen />,
  VERIFY_EMAIL: () => <VerifyCodeScreen purpose="VERIFY_EMAIL" />,
  LOGIN: () => <LoginScreen />,
  VERIFY_LOGIN: () => <VerifyCodeScreen purpose="LOGIN" />,
};

// The session's one start per page load — what the URL asked for, the StrictMode guard — is the auth router's view model since
// frontend-framework-oo P1 (§28.293): its flag was a module-level `let` here.
function AuthRouter(): React.ReactElement {
  const [{ screen }] = useViewModel((views) => views.authRouter());

  if (screen === 'GAME') {
    // The LiveKit client FACTORY reaches AppRoot unresolved (a host resource on the Framework's context):
    // creating a client downloads the livekit-client SDK chunk, which happens only when the player opts
    // into video, never on a game-screen visit as such.
    return <AppRoot />;
  }

  const renderScreen = SCREEN_RENDERERS[screen];
  return (
    <Suspense fallback={<LoadingBoundary />}>
      {renderScreen ? renderScreen() : <WelcomeScreen />}
    </Suspense>
  );
}

const App: React.FC = () => (
  <ErrorBoundary>
    {/*
      The view-model factory is the composition root's own object, so its provider wraps the maintenance banner too (frontend-framework-oo
      P5, §28.297): the banner's model binds through it. The client core's provider stays below the banner — the banner that says the
      backend is unreachable must not itself depend on it (the 2026-08-17 white screen).
    */}
    <ViewModelProvider factory={frameworkViews}>
      <MaintenanceBanner checkHealth={checkServerHealth} />
      {/*
        The provider wraps EVERY screen, not just GAME (fixed 2026-08-17).

        It used to wrap only the GAME branch's <AppRoot />, which was correct while the auth screens
        got their collaborators from module singletons. It stopped being correct the moment those
        screens were repointed at the registry: WelcomeScreen, LoginScreen, RegisterScreen,
        VerifyCodeScreen, EnterCodeScreen and PaymentSuccessScreen all call useComponentFramework()
        now, and all of them render on this side of the tree. The result was a white screen on load —
        "useComponentFramework must be used within <ComponentFrameworkProvider>" out of WelcomeScreen,
        caught by the e2e warmup, invisible to every unit test because those render the component
        inside a provider or mock the hook outright.

        Hoisting costs nothing at load: this module already imports apolloClient and the Apollo
        adapters statically (see the eager-registry note above), and bootstrap makes no network call —
        it resolves apolloClientFactory, which nothing registers. The lazy chunks stay lazy.
      */}
      <ComponentFrameworkProvider framework={frontendGraph.framework} host={frameworkHost}>
        {/* Inside the provider since strict-ports FE4b: it reads the client view and calls use cases. */}
        <CookieConsentProvider />
        <Suspense fallback={<LoadingBoundary />}>
          <AuthRouter />
        </Suspense>
      </ComponentFrameworkProvider>
    </ViewModelProvider>
  </ErrorBoundary>
);

export default App;
